import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, Loader2, X, Mail, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Nav from './Nav';
import axiosInstance from '../services/axiosInstance';
import Footer from '../components/Footer';
import GoogleSignIn from './GoogleSignIn';

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    
    // Forgot password states
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
    const [forgotPasswordError, setForgotPasswordError] = useState("");
    
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setMessage("");

        try {
            const response = await axiosInstance.post('/auth/login', {
                email,
                password
            });

            if (response.data.success) {
                setMessage("Login successful!");
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify({
                    name: response.data.user.name,
                    email: response.data.user.email,
                    role: response.data.user.role
                }));

                // Check if user is admin and redirect accordingly
                if (response.data.user.role === 'admin') {
                    // Store admin token for admin dashboard access
                    localStorage.setItem('admin_token', response.data.token);
                    // Navigate to admin dashboard within the frontend
                    navigate('/admin-dashboard');
                } else {
                    // Navigate to regular dashboard for non-admin users
                    navigate('/dashboard');
                }
            } else {
                setError(response.data.message || "Login failed. Please try again.");
            }
        } catch (err) {
            // Handle specific error cases
            const errorMessage = err.response?.data?.message || "Connection error. Please try again later.";
            
            if (errorMessage.includes("not found") || errorMessage.includes("User not found")) {
                setError("No account found with this email. Please check your email or sign up for a new account.");
            } else if (errorMessage.includes("not verified") || errorMessage.includes("Email not verified")) {
                setError("Your email is not verified. Please check your inbox for the verification email or request a new one.");
            } else if (errorMessage.includes("Incorrect password") || errorMessage.includes("wrong password")) {
                setError("Incorrect password. Please check your password and try again.");
            } else {
                setError(errorMessage);
            }
            
            console.error("Login error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotPasswordLoading(true);
        setForgotPasswordError("");
        setForgotPasswordMessage("");

        try {
            const response = await axiosInstance.post('/auth/forgot-password', {
                email: forgotPasswordEmail
            });

            if (response.data.success) {
                setForgotPasswordMessage("Password reset link sent to your email!");
                setForgotPasswordEmail("");
                // Close modal after 3 seconds
                setTimeout(() => {
                    setShowForgotPassword(false);
                    setForgotPasswordMessage("");
                }, 3000);
            } else {
                setForgotPasswordError(response.data.message || "Failed to send reset link. Please try again.");
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Connection error. Please try again later.";
            
            if (errorMessage.includes("not found") || errorMessage.includes("User not found")) {
                setForgotPasswordError("No account found with this email address. Please check your email or sign up for a new account.");
            } else if (errorMessage.includes("not verified") || errorMessage.includes("Email not verified")) {
                setForgotPasswordError("Your email is not verified. Please verify your email first before requesting a password reset.");
            } else {
                setForgotPasswordError(errorMessage);
            }
            
            console.error("Forgot password error:", err);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const clearForgotPasswordModal = () => {
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
        setForgotPasswordError("");
        setForgotPasswordMessage("");
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <Nav />
            
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
                <Card className="w-full max-w-md bg-black/80 backdrop-blur-xl border-gray-800 shadow-2xl">
                    <CardHeader className="space-y-2 text-center">
                        <CardTitle className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                            Welcome back
                        </CardTitle>
                        <CardDescription className="text-gray-400 text-lg">
                            Enter your credentials to continue
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        {error && (
                            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                                <div className="flex items-start space-x-2">
                                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-300 font-medium">
                                    Email address <span className="text-red-400">*</span>
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 transition-colors"
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password" className="text-gray-300 font-medium">
                                        Password <span className="text-red-400">*</span>
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPassword(true)}
                                        className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 transition-colors pr-10"
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-white text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {loading ? (
                                    <div className="flex items-center space-x-2">
                                        <Loader2 className="animate-spin" size={20} />
                                        <span>Signing in...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center space-x-2">
                                        <span>Sign in</span>
                                        <ArrowRight size={18} />
                                    </div>
                                )}
                            </Button>
                        </form>

                        <div className="relative">
                            <Separator className="bg-gray-700" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="bg-black px-4 text-gray-400 text-sm">or</span>
                            </div>
                        </div>

                        <GoogleSignIn 
                            onError={(error) => setError(error)}
                            className="w-full"
                        />

                        {message && (
                            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm text-center">
                                {message}
                            </div>
                        )}

                        <div className="text-center text-gray-400">
                            <span>
                                Don't have an account?{' '}
                                <a href="/signup" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                                    Sign up
                                </a>
                            </span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="relative w-full max-w-md bg-black/95 border border-gray-800 rounded-lg shadow-2xl p-6">
                        <button 
                            onClick={clearForgotPasswordModal}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>
                        
                        <div className="text-center mb-6">
                            <div className="mx-auto w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                                <Mail size={32} className="text-blue-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">
                                Forgot Password?
                            </h2>
                            <p className="text-gray-400">
                                Enter your email address and we'll send you a link to reset your password.
                            </p>
                        </div>

                        {forgotPasswordError && (
                            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center mb-6">
                                <div className="flex items-center justify-center space-x-2">
                                    <AlertCircle size={16} />
                                    <span>{forgotPasswordError}</span>
                                </div>
                            </div>
                        )}

                        {forgotPasswordMessage && (
                            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm text-center mb-6">
                                {forgotPasswordMessage}
                            </div>
                        )}

                        <form onSubmit={handleForgotPassword} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="forgot-email" className="text-gray-300 font-medium">
                                    Email address <span className="text-red-400">*</span>
                                </Label>
                                <Input
                                    id="forgot-email"
                                    type="email"
                                    value={forgotPasswordEmail}
                                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                    className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 transition-colors"
                                    placeholder="Enter your email address"
                                    required
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={forgotPasswordLoading}
                                className="w-full bg-white text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {forgotPasswordLoading ? (
                                    <div className="flex items-center space-x-2">
                                        <Loader2 className="animate-spin" size={20} />
                                        <span>Sending...</span>
                                    </div>
                                ) : (
                                    'Send Reset Link'
                                )}
                            </Button>
                        </form>

                        <div className="text-center mt-4">
                            <button
                                onClick={clearForgotPasswordModal}
                                className="text-gray-400 hover:text-white transition-colors text-sm"
                            >
                                Back to login
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <Footer />
        </div>
    );
};

export default Login;